import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useStore from "../../Store/Store";
import { logger } from "../../utils/Logger";
import type { SuggestedFile } from "./OnboardingWorkflow";

interface LoadingStatusBarProps {
  indexingProgress: IndexingProgress;
  repoTree: any;
  suggestedFiles: SuggestedFile[] | null;
  usecaseFeatures?: unknown;
}

const LoadingStatusBar: React.FC<LoadingStatusBarProps> = ({
  indexingProgress,
  repoTree,
  suggestedFiles,
  usecaseFeatures,
}) => {
  const theme = useTheme();
  const {
    currentRepo,
    onboardingWorkflowState,
    simulations,
    triggerOnboardingWorkflow,
    downloadProgress,
  } = useStore((state) => ({
    currentRepo: state.currentRepo,
    onboardingWorkflowState: state.onboardingWorkflowState,
    simulations: state.simulationsState.simulations,
    triggerOnboardingWorkflow: state.triggerOnboardingWorkflow,
    downloadProgress: state.downloadProgress,
  }));

  const [now, setNow] = useState<number>(Date.now());
  // Persist progress values to prevent reset on completion
  const [persistedProgress, setPersistedProgress] = useState<{
    step1: number;
    step2: number;
    step3: number;
    step4: number;
    step5: number;
  }>({ step1: 0, step2: 0, step3: 0, step4: 0, step5: 0 });

  // Check if there are any existing simulations (AI-generated or manual)
  const hasExistingSimulations = Object.keys(simulations).length > 0;

  // AI workflow should run if there are no existing simulations OR if manually triggered
  const shouldRunAIWorkflow =
    !hasExistingSimulations || triggerOnboardingWorkflow;

  // Define the steps
  const steps = [
    {
      id: 1,
      title: "Downloading repository",
      description: "Fetching source code from repository",
      isVisible: true,
    },
    {
      id: 2,
      title: "Indexing content",
      description: "Building search index for code navigation",
      isVisible: true,
    },
    {
      id: 3,
      title: "Analyzing files",
      description: "Identifying essential repository files",
      isVisible: shouldRunAIWorkflow,
    },
    {
      id: 4,
      title: "Understanding features",
      description: `Analyzing primary use-cases of ${currentRepo}`,
      isVisible: shouldRunAIWorkflow,
    },
    {
      id: 5,
      title: "Preparing simulations",
      description: "Setting up interactive diagrams",
      isVisible: shouldRunAIWorkflow,
    },
  ];

  const visibleSteps = steps.filter((step) => step.isVisible);
  const totalSteps = visibleSteps.length;
  const stepWidth = 100 / totalSteps; // Each step takes up this percentage of the total bar

  // Update timer for progress calculation
  useEffect(() => {
    let timer: number;
    // Run while any step is in progress
    const isAnyStepInProgress =
      downloadProgress.isDownloading ||
      (onboardingWorkflowState.step1.isStarted &&
        !onboardingWorkflowState.step1.isCompleted) ||
      (onboardingWorkflowState.step2.isStarted &&
        !onboardingWorkflowState.step2.isCompleted) ||
      (onboardingWorkflowState.step3.isStarted &&
        !onboardingWorkflowState.step3.isCompleted);

    if (isAnyStepInProgress) {
      timer = window.setInterval(() => setNow(Date.now()), 1000);
    }
    return () => {
      clearInterval(timer);
    };
  }, [onboardingWorkflowState, downloadProgress.isDownloading]);

  // Update persisted progress values when steps change
  useEffect(() => {
    setPersistedProgress((prevProgress) => {
      const newProgress = { ...prevProgress };

      // Update step 1 progress (downloading)
      if (
        downloadProgress.phase === "idle" &&
        !downloadProgress.isDownloading
      ) {
        newProgress.step1 = 100;
      } else if (downloadProgress.isDownloading) {
        let progress = 0;
        switch (downloadProgress.phase) {
          case "downloading":
            if (
              downloadProgress.totalBytes &&
              downloadProgress.bytesDownloaded
            ) {
              progress = Math.min(
                (downloadProgress.bytesDownloaded /
                  downloadProgress.totalBytes) *
                  100,
                100
              );
            } else {
              progress = 10; // Show some progress even without specific bytes
            }
            break;
          case "extracting":
            progress = 80;
            break;
          case "processing":
            if (
              downloadProgress.totalFiles &&
              downloadProgress.filesProcessed
            ) {
              progress = Math.min(
                80 +
                  (downloadProgress.filesProcessed /
                    downloadProgress.totalFiles) *
                    20,
                100
              );
            } else {
              progress = 90;
            }
            break;
          default:
            progress = 5;
        }
        newProgress.step1 = Math.max(progress, prevProgress.step1);
      }

      // Update step 2 progress (indexing)
      if (onboardingWorkflowState.step1.isCompleted) {
        newProgress.step2 = 100;
      } else if (
        onboardingWorkflowState.step1.isStarted &&
        indexingProgress.total > 0
      ) {
        const progress = Math.min(
          (indexingProgress.current / indexingProgress.total) * 100,
          100
        );
        newProgress.step2 = Math.max(progress, prevProgress.step2);
      }

      // Update step 3 progress (essential files analysis)
      if (onboardingWorkflowState.step2.isCompleted) {
        newProgress.step3 = 100;
      } else if (
        onboardingWorkflowState.step2.isStarted &&
        onboardingWorkflowState.step2.startTime
      ) {
        const elapsed = now - onboardingWorkflowState.step2.startTime;
        const timeBasedProgress = Math.min((elapsed / 18000) * 90, 90);
        newProgress.step3 = Math.max(timeBasedProgress, prevProgress.step3);
      }

      // Update step 4 progress (use-case features analysis)
      if (onboardingWorkflowState.step3.isCompleted) {
        newProgress.step4 = 100;
      } else if (
        onboardingWorkflowState.step3.isStarted &&
        onboardingWorkflowState.step3.startTime
      ) {
        const elapsed = now - onboardingWorkflowState.step3.startTime;
        const timeBasedProgress = Math.min((elapsed / 18000) * 90, 90);
        newProgress.step4 = Math.max(timeBasedProgress, prevProgress.step4);
      }

      // Update step 5 progress (simulation generation)
      if (shouldRunAIWorkflow && onboardingWorkflowState.step3.isCompleted) {
        newProgress.step5 = 100;
      } else if (
        shouldRunAIWorkflow &&
        onboardingWorkflowState.step2.isCompleted
      ) {
        newProgress.step5 = 50;
      }

      return newProgress;
    });
  }, [
    onboardingWorkflowState,
    indexingProgress,
    downloadProgress,
    shouldRunAIWorkflow,
    now,
  ]);

  // Don't show if no repo is selected
  if (!currentRepo) {
    return null;
  }

  // Show if downloading, indexing, or AI workflow is running
  const shouldShow =
    downloadProgress.isDownloading ||
    (onboardingWorkflowState.step1.isStarted &&
      !onboardingWorkflowState.step1.isCompleted) ||
    (shouldRunAIWorkflow &&
      onboardingWorkflowState.step2.isStarted &&
      !onboardingWorkflowState.step2.isCompleted) ||
    (shouldRunAIWorkflow &&
      onboardingWorkflowState.step3.isStarted &&
      !onboardingWorkflowState.step3.isCompleted);

  if (!shouldShow) {
    return null;
  }

  // Calculate which step is currently active
  const getCurrentStep = () => {
    if (downloadProgress.isDownloading) return 1;
    if (
      onboardingWorkflowState.step1.isStarted &&
      !onboardingWorkflowState.step1.isCompleted
    )
      return 2;
    if (
      shouldRunAIWorkflow &&
      onboardingWorkflowState.step2.isStarted &&
      !onboardingWorkflowState.step2.isCompleted
    )
      return 3;
    if (
      shouldRunAIWorkflow &&
      onboardingWorkflowState.step3.isStarted &&
      !onboardingWorkflowState.step3.isCompleted
    )
      return 4;
    if (
      shouldRunAIWorkflow &&
      onboardingWorkflowState.step2.isCompleted &&
      !onboardingWorkflowState.step3.isCompleted
    )
      return 5;
    return 1;
  };

  const currentStep = getCurrentStep();
  const currentStepInfo = visibleSteps.find((step) => step.id === currentStep);

  // Calculate overall progress
  const getStepProgress = (stepId: number): number => {
    const stepKey = `step${stepId}` as keyof typeof persistedProgress;
    return persistedProgress[stepKey] || 0;
  };

  // Calculate the total progress width
  const calculateTotalProgress = (): number => {
    let totalProgress = 0;

    visibleSteps.forEach((step, index) => {
      const stepProgress = getStepProgress(step.id);
      const stepContribution = (stepProgress / 100) * stepWidth;
      totalProgress += stepContribution;
    });

    return Math.min(totalProgress, 100);
  };

  const totalProgressWidth = calculateTotalProgress();

  // Count completed steps
  const completedSteps = visibleSteps.filter(
    (step) => getStepProgress(step.id) >= 100
  ).length;

  return (
    <Box
      position={"fixed"}
      bottom={"-10vh"}
      left={"-3vw"}
      width={"calc(100% - 20px)"}
      maxWidth={undefined}
      margin={"0"}
      bgcolor="background.paper"
      p={3}
      boxShadow={0}
      borderRadius={6}
      zIndex={10000002}
      style={{ padding: "20px 20px 30px 20px" }}
    >
      <Box width="100%" textAlign="center" mb={3}>
        <Typography
          variant="h5"
          mb={2}
          sx={{
            fontWeight: 600,
            color: theme.custom.darkerGrey,
          }}
        >
          Setting up {currentRepo}
        </Typography>

        {currentStepInfo && (
          <Typography
            variant="body1"
            mb={3}
            sx={{
              opacity: 0.8,
              fontWeight: "normal",
              color: theme.custom.darkerGrey,
              lineHeight: 1.5,
            }}
          >
            {currentStepInfo.description}...
          </Typography>
        )}

        {/* Single segmented progress bar */}
        <Box
          position="relative"
          height={12}
          borderRadius={2}
          overflow="hidden"
          bgcolor={theme.custom.lightGrey}
          mb={3}
          mx="auto"
          width="80%"
        >
          {/* Progress fill */}
          <Box
            position="absolute"
            left={0}
            top={0}
            bottom={0}
            width={`${totalProgressWidth}%`}
            bgcolor={theme.custom.pink}
            sx={{
              transition: "width 0.5s ease, box-shadow 0.5s ease",
              ...(totalProgressWidth >= 80
                ? { boxShadow: `0 0 12px ${theme.custom.pink}` }
                : {}),
            }}
          />

          {/* Step dividers */}
          {visibleSteps.slice(0, -1).map((_, index) => (
            <Box
              key={index}
              position="absolute"
              left={`${(index + 1) * stepWidth}%`}
              top={0}
              bottom={0}
              width="1px"
              bgcolor="white"
              sx={{ opacity: 0.3 }}
            />
          ))}
        </Box>

        {/* Step indicators */}
        <Box
          display="flex"
          justifyContent="space-between"
          width="80%"
          mx="auto"
          mb={2}
        >
          {visibleSteps.map((step, index) => {
            const stepProgress = getStepProgress(step.id);
            const isCompleted = stepProgress >= 100;
            const isActive = step.id === currentStep;

            return (
              <Box
                key={step.id}
                display="flex"
                flexDirection="column"
                alignItems="center"
                width={`${stepWidth}%`}
              >
                <Box
                  width={16}
                  height={16}
                  borderRadius="50%"
                  bgcolor={
                    isCompleted
                      ? theme.custom.pink
                      : isActive
                      ? theme.custom.lighterPink
                      : theme.custom.lightGrey
                  }
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  mb={1}
                  sx={{
                    transition: "all 0.3s ease",
                    ...(isCompleted && {
                      boxShadow: `0 0 8px ${theme.custom.pink}`,
                    }),
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "10px",
                      fontWeight: "bold",
                      color:
                        isCompleted || isActive
                          ? "white"
                          : theme.custom.darkerGrey,
                    }}
                  >
                    {step.id}
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "11px",
                    color: isActive
                      ? theme.custom.darkerGrey
                      : theme.custom.lightGrey,
                    fontWeight: isActive ? "medium" : "normal",
                    textAlign: "center",
                    lineHeight: 1.2,
                  }}
                >
                  {step.title}
                </Typography>
              </Box>
            );
          })}
        </Box>

        <Typography
          variant="body2"
          sx={{
            opacity: 0.7,
            color: theme.custom.darkerGrey,
            fontSize: "14px",
          }}
        >
          {Math.round(totalProgressWidth)}% complete • Step {completedSteps + 1}{" "}
          of {totalSteps}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            opacity: 0.7,
            fontStyle: "italic",
            color: theme.custom.darkerGrey,
            fontSize: "12px",
          }}
        >
          It takes on average 3 minutes to complete a fresh repo onboarding due
          to LLM reasoning time.
        </Typography>
      </Box>
    </Box>
  );
};

export default LoadingStatusBar;
